<script>
import date from '@/mixins/date'
import messages from '@/mixins/messages'
import ListingResource from '@/resources/listing'
import DeveloperProfileResource from '../resources/developerprofile'
import JobPosting from '@/components/JobPosting.vue'
import ProfileCell from '@/components/ProfileCell.vue'
import ProfileSummary from '@/components/ProfileSummary.vue'

export default {
    name: 'RecruitJob',
    components: {
        JobPosting,
        ProfileCell,
        ProfileSummary
    },
    mixins: [date, messages],
    data() {
        return {
            loading: true,
            publishing: false,
            listingResource: null,
            selectedProfileResource: null,
            profileModalVisible: false,
            interested: {
                profileResources: [],
                pageData: null,
                pageCurrent: 1
            },
            passed: {
                profileResources: [],
                pageData: null,
                pageCurrent: 1
            },
            applied: {
                profileResources: [],
                pageData: null,
                pageCurrent: 1
            }
        }
    },
    props: {
        companyResource: {
            type: Object,
        },
    },
    watch: {
        'interested.profileResources': () => {
            window.scroll(0, 0)
        },
        'passed.profileResources': () => {
            window.scroll(0, 0)
        },
    },
    computed: {
        breadcrumbs() {
            return [{
                text: 'Job Listings',
                to: { name: 'recruitHome', params: { companyId: this.companyResource.id } }
            }, {
                text: `${this.listingResource} (${this.listingResource.status.text})`
            }]
        }
    },
    methods: {
        showPublishConfirm() {
            if(!this.companyResource.canCreateListing()) {
                this.$router.push({ name: 'upgrade', params: { companyId: this.companyResource.id }, query: { next: this.$route.path } })
                return false
            }

            this.$refs.confirmModal.show()
        },
        async publish() {
            try {
                this.publishing = true
                await this.listingResource.activate()
                this.addSuccess(`${this.listingResource.attributes.title} has been successfully published`)
            } catch(e) {
                this.addError(this.parseAPIErrorsOrRethrow(e))
            } finally {
                this.publishing = false
            }
        },
        async searchInterested() {
            let result = await DeveloperProfileResource.list({ query: { interested: this.listingResource.id } })
            this.interested.profileResources = result.resources
            this.interested.pageData = result
            this.interested.pageCurrent = 1
        },
        async searchPassed() {
            let result = await DeveloperProfileResource.list({ query: { passed: this.listingResource.id } })
            this.passed.profileResources = result.resources
            this.passed.pageData = result
            this.passed.pageCurrent = 1
        },
        async searchApplicants() {
            let result = await DeveloperProfileResource.list({ query: { applied: this.listingResource.id } })
            this.applied.profileResources = result.resources
            this.applied.pageData = result
            this.applied.pageCurrent = 1
        },
        async onInterestedPageChange(num) {
            this.interested.pageData = await this.interested.pageData.goToPage(num)
            this.interested.profileResources = this.interested.pageData.resources
            this.interested.pageCurrent = num
        },
        async onPassedPageChange(num) {
            this.passed.pageData = await this.passed.pageData.goToPage(num)
            this.passed.profileResources = this.passed.pageData.resources
            this.passed.pageCurrent = num
        },
        async onAppliedPageChange(num) {
            this.applied.pageData = await this.applied.pageData.goToPage(num)
            this.applied.profileResources = this.applied.pageData.resources
            this.applied.pageCurrent = num
        },
        async onInterested(profileResource) {
            await this.listingResource.interested(profileResource.id)
        },
        async onPass(profileResource) {
            await this.listingResource.pass(profileResource.id)
        },
        async sendMessageToProfile(profileResource) {
            let userResourceManager = profileResource.rel('user')
            await userResourceManager.resolve()
            this.$store.commit('composeMessage', userResourceManager.resource)
            this.profileModalVisible = false
        },
        selectProfile(profileResource) {
            this.selectedProfileResource = profileResource
            this.profileModalVisible = true
        },
    },
    async mounted() {
        let listingId = this.$route.params.listingId
        this.listingResource = await ListingResource.detail(listingId, { resolveRelated: true })
        await this.searchInterested()
        await this.searchPassed()
        await this.searchApplicants()
        this.loading = false
    },
}
</script>

<template>
    <div>
        <messages />

        <b-breadcrumb class="mt-3" :items="breadcrumbs" v-if="!loading" />

        <b-card no-body v-if="!loading && listingResource" class="mt-3" v-page-title="String(listingResource)">
            
            <div class="floating-buttons d-none d-lg-block d-xl-block">
                <b-button-group class="floating-buttons">
                    <b-button variant="primary" :disabled="listingResource.status.variant !== 'success'" :to="{ name: 'recruitSource', params: { companyId: listingResource.attributes.company }, query: { listing: listingResource.id } }">Browse Candidates</b-button>
                </b-button-group>
            </div>

            <b-tabs card :small="true">
                <b-tab title="Applicants" :disabled="!applied.profileResources.length">
                    <b-card v-for="(profileResource, i) in applied.profileResources" :key="i" class="mb-3">
                        <profile-cell :profile="profileResource" :listing="listingResource" @click="selectProfile(profileResource)">
                            <b-button size="sm" variant="outline-secondary" @click="sendMessageToProfile(profileResource)"><b-icon icon="envelope" /> Send Message</b-button>
                        </profile-cell>
                    </b-card>

                    <b-pagination
                        v-if="applied.pageData && applied.pageData.pages > 1"
                        @input="onAppliedPageChange"
                        :value="applied.pageCurrent"
                        :total-rows="applied.pageData.count"
                        :per-page="applied.pageData.pageSize"
                        prev-text="Prev"
                        next-text="Next"
                        align="center"
                        hide-ellipsis
                    >
                    </b-pagination>
                </b-tab>

                <b-tab title="Interested" :disabled="!interested.profileResources.length">
                    <b-card v-for="(profileResource, i) in interested.profileResources" :key="i" class="mb-3">
                        <profile-cell :profile="profileResource" :listing="listingResource" @click="selectProfile(profileResource)">
                            <b-button size="sm" variant="outline-secondary" @click="sendMessageToProfile(profileResource)"><b-icon icon="envelope" /> Send Message</b-button>
                        </profile-cell>
                    </b-card>

                    <b-pagination
                        v-if="interested.pageData && interested.pageData.pages > 1"
                        @input="onInterestedPageChange"
                        :value="interested.pageCurrent"
                        :total-rows="interested.pageData.count"
                        :per-page="interested.pageData.pageSize"
                        prev-text="Prev"
                        next-text="Next"
                        align="center"
                        hide-ellipsis
                    >
                    </b-pagination>
                </b-tab>

                <b-tab title="Passed" :disabled="!passed.profileResources.length">
                    <b-card v-for="(profileResource, i) in passed.profileResources" :key="i" class="mb-3">
                        <profile-cell :profile="profileResource" :listing="listingResource" @click="selectProfile(profileResource)">
                        </profile-cell>
                    </b-card>

                    <b-pagination
                        v-if="passed.pageData && passed.pageData.pages > 1"
                        @input="onPassedPageChange"
                        :value="passed.pageCurrent"
                        :total-rows="passed.pageData.count"
                        :per-page="passed.pageData.pageSize"
                        prev-text="Prev"
                        next-text="Next"
                        align="center"
                        hide-ellipsis
                    >
                    </b-pagination>
                </b-tab>

                <b-tab title="View/Edit">
                    <job-posting
                        :listing-resource="listingResource"
                        no-title
                        show-owner-controls>
                        <template slot="details-header">
                            <b-button variant="primary" v-if="listingResource.status.text === 'Paused'" class="mr-2">Resume</b-button>
                            <b-button variant="primary" v-if="listingResource.status.text === 'Active'" class="mr-2">Unpublish</b-button>
                            <b-button variant="primary" v-else-if="!listingResource.attributes['expires']" @click="showPublishConfirm" class="mr-2" :disabled="publishing">Publish</b-button>
                            
                            <b-button :to="{ name: 'recruitJobEdit', params: { companyId: companyResource.id, listingId: listingResource.id } }">Edit</b-button>
                            <hr />
                        </template>
                    </job-posting>
                </b-tab>
            </b-tabs>
        </b-card>

        <span v-else>
            Loading...
        </span>

        <b-modal header-bg-variant="secondary" header-text-variant="light" centered no-fade title="Confirm Publish" ref="confirmModal" @ok="publish" v-if="listingResource">
            Would you like to publish <em>{{ listingResource.attributes.title }}</em>? Your ad will be active for 30 days. You can suspend your ad listing at any time.
        </b-modal>

        <b-modal v-model="profileModalVisible" size="xl" :title="`Reviewing ${selectedProfileResource}`" no-fade centered scrollable>
            <template #modal-footer>
                <b-button-group>
                    <b-button @click="onInterested(selectedProfileResource)">Interested</b-button>
                    <b-button @click="onPass(selectedProfileResource)">Pass</b-button>
                </b-button-group>
                <b-button-group class="float-right">
                    <b-button variant="outline-secondary" @click="sendMessageToProfile(selectedProfileResource)"><b-icon icon="envelope" /> Send Message</b-button>
                </b-button-group>
            </template>
            <profile-summary :profile="selectedProfileResource" :listing="listingResource">
            </profile-summary>
        </b-modal>
        
    </div>
</template>

<style lang="scss">
.floating-buttons {
    position: absolute;
    right: 10px;
    top: 10px;
    width: 200px;
}
</style>
